import { erpHeadingAccentClass } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';

export type RfqPageUi = ReturnType<typeof createRfqPageUi>;

export function createRfqPageUi(isDark: boolean) {
  const fieldBase = isDark
    ? 'w-full px-3 py-2 text-[13px] font-medium bg-[#0f172a] border border-white/[0.08] rounded-md text-slate-100 placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:border-[#0088cc]/45 focus:ring-1 focus:ring-[#0088cc]/25 transition-colors'
    : 'w-full px-3 py-2 text-[13px] font-medium bg-white border border-[#cbd5e1] rounded-md text-[#0f172a] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:border-[#0088cc] focus:ring-1 focus:ring-[#0088cc]/20 transition-colors';

  const tableFieldBase = isDark
    ? 'w-full bg-[#0f172a] border border-white/[0.08] rounded px-2.5 py-1.5 text-[12px] font-medium text-slate-100 focus:outline-none focus:border-[#0088cc]/40 placeholder:text-[hsl(var(--muted-foreground))] uppercase tracking-tight'
    : 'w-full bg-white border border-[#cbd5e1] rounded px-2.5 py-1.5 text-[12px] font-medium text-[#0f172a] focus:outline-none focus:border-[#0088cc] placeholder:text-[hsl(var(--muted-foreground))] uppercase tracking-tight';

  return {
    isDark,
    page: cn(
      'w-full min-h-screen px-4 pt-4 pb-32 sm:px-5 sm:pt-5 sm:pb-36',
      isDark ? 'bg-[#030712] text-slate-100' : 'bg-[#0088cc]/5 text-[hsl(var(--foreground))]'
    ),
    breadcrumbMuted: isDark ? 'text-[hsl(var(--muted-foreground))]' : 'text-[hsl(var(--muted-foreground))]',
    breadcrumbSep: isDark ? 'text-[hsl(var(--muted-foreground))]' : 'text-[#0088cc]/45',
    breadcrumbLink: isDark
      ? 'text-[hsl(var(--muted-foreground))] hover:text-[#38bdf8]'
      : 'text-[hsl(var(--muted-foreground))] hover:text-[#0088cc]',
    breadcrumbActive: erpHeadingAccentClass,
    hero: cn(
      'relative mb-4 overflow-hidden rounded-xl ring-1',
      isDark
        ? 'border border-white/[0.08] bg-[#0f172a] shadow-[0_4px_20px_rgba(0,0,0,0.3)] ring-[#0088cc]/15'
        : 'border border-[#0088cc]/20 bg-white shadow-sm ring-[#0088cc]/10'
    ),
    heroGradient: cn(
      'pointer-events-none absolute inset-0 bg-gradient-to-br',
      isDark ? 'from-[#0088cc]/10 via-transparent to-[#1e293b]/40' : 'from-[#0088cc]/6 via-transparent to-blue-50/50'
    ),
    heroGlow: cn(
      'pointer-events-none absolute -left-24 top-0 h-48 w-48 rounded-full blur-3xl',
      isDark ? 'bg-[#0088cc]/8' : 'bg-[#0088cc]/5'
    ),
    heroRibbon: cn(
      'relative border-b px-4 py-1.5 sm:px-5 bg-gradient-to-r from-[#0088cc]/10 to-transparent',
      isDark ? 'border-white/[0.06]' : 'border-[#0088cc]/20'
    ),
    heroRibbonText: isDark ? 'text-[#0088cc]/65' : 'text-[#0088cc]/90',
    title: cn(
      'text-xl font-bold tracking-tight sm:text-2xl',
      isDark ? 'text-white' : 'text-[hsl(var(--foreground))]'
    ),
    subtitle: cn('text-xs font-medium', isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]'),
    draftBadge: cn(
      'inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em]',
      isDark ? 'border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.15)] text-[#fbbf24]' : 'border-[#fbbf24] bg-[#fef3c7] text-[#92400e]'
    ),
    cmdBtn:
      'inline-flex h-9 min-h-[36px] items-center justify-center gap-1.5 rounded-lg px-4 text-[11px] font-bold uppercase tracking-wider transition-all',
    abortBtn: isDark
      ? 'border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:border-rose-500/45 hover:bg-rose-500/15'
      : 'border border-[#fca5a5] bg-red-50 text-[#b91c1c] hover:border-red-300 hover:bg-red-100',
    saveDraftBtn: isDark
      ? 'border border-white/[0.12] bg-[#111827] text-slate-200 hover:border-white/20 hover:bg-[#1e293b]'
      : 'border border-[#cbd5e1] bg-white text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5',
    sendBtn: cn(
      'border border-[#0088cc]/50 bg-[#0088cc] px-6 text-white shadow-md hover:bg-[#0077b3]',
      isDark ? 'shadow-[#0088cc]/20' : 'shadow-[#0088cc]/15'
    ),
    section: cn(
      'mb-4 rounded-lg p-4 sm:p-5',
      isDark
        ? 'border border-white/[0.08] bg-[#111827] shadow-[0_2px_12px_rgba(0,0,0,0.2)]'
        : 'border border-[#0088cc]/20 bg-white shadow-sm'
    ),
    sectionHeader: cn('flex items-center gap-2 mb-3 border-b pb-2', isDark ? 'border-white/[0.08]' : 'border-[#0088cc]/20'),
    sectionTitle: cn('text-sm font-bold uppercase tracking-wider', isDark ? 'text-white' : 'text-[hsl(var(--foreground))]'),
    label: cn(
      'text-[10px] font-bold uppercase tracking-wider',
      isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]'
    ),
    field: fieldBase,
    fieldSelect: cn(fieldBase, 'cursor-pointer'),
    fieldDisabled: cn(
      'w-full px-3 py-2 text-[13px] font-mono rounded-md cursor-not-allowed uppercase tracking-tighter',
      isDark
        ? 'bg-[#0f172a]/70 border border-white/[0.06] text-[hsl(var(--muted-foreground))]'
        : 'bg-[#0088cc]/5 border border-[#0088cc]/20 text-[#0088cc]/65'
    ),
    fieldDisabledPlain: cn(
      'w-full px-3 py-2 text-[13px] font-mono rounded-md cursor-not-allowed',
      isDark
        ? 'bg-[#0f172a]/70 border border-white/[0.06] text-[hsl(var(--muted-foreground))]'
        : 'bg-[#0088cc]/5 border border-[#0088cc]/20 text-[#0088cc]/65'
    ),
    divider: isDark ? 'border-white/[0.08]' : 'border-[#0088cc]/20',
    urgencyNormal: isDark
      ? 'border-white/[0.08] bg-[#0f172a] text-slate-100 focus:border-[#0088cc]/45 focus:ring-[#0088cc]/25'
      : 'border-[#cbd5e1] bg-white text-[#0f172a] focus:border-[#0088cc] focus:ring-[#0088cc]/20',
    urgencyCritical: isDark
      ? 'border-red-500/40 bg-red-500/10 text-red-300 focus:border-red-500/60 focus:ring-red-500/25'
      : 'border-red-300 bg-red-50 text-red-700 focus:border-red-400 focus:ring-red-200',
    budgetPanel: cn(
      'flex flex-col justify-center rounded-md border p-3',
      isDark ? 'border-white/[0.08] bg-[#0f172a]/60' : 'border-[#0088cc]/20 bg-[#0088cc]/5'
    ),
    dropzone: cn(
      'rounded-lg border-2 border-dashed p-5 text-center transition-all',
      isDark
        ? 'border-white/[0.1] bg-[#0f172a]/50 hover:border-[#0088cc]/25 hover:bg-[#0f172a]/80'
        : 'border-[#cbd5e1] bg-[#0088cc]/5/80 hover:border-[#0088cc]/40 hover:bg-blue-50/30'
    ),
    dropzoneIcon: cn(
      'mx-auto flex h-11 w-11 items-center justify-center rounded-full border shadow-sm',
      isDark ? 'border-white/[0.08] bg-[#111827]' : 'border-[#0088cc]/20 bg-white'
    ),
    dropzoneTitle: isDark ? 'text-slate-200' : 'text-[hsl(var(--foreground))]',
    dropzoneHint: isDark ? 'text-[hsl(var(--muted-foreground))]' : 'text-[hsl(var(--muted-foreground))]',
    attachmentCard: cn(
      'group flex items-center justify-between rounded-lg border px-3 py-2.5 transition-all hover:border-[#0088cc]/30',
      isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-white'
    ),
    attachmentIcon: cn(
      'flex h-8 w-8 items-center justify-center rounded-md border',
      isDark ? 'border-white/[0.08] bg-[#111827] text-[#38bdf8]' : 'border-[#0088cc]/20 bg-[#0088cc]/5 text-[#0088cc]'
    ),
    attachmentName: isDark ? 'text-slate-200' : 'text-[hsl(var(--foreground))]',
    vendorPanel: (hasError: boolean) =>
      cn(
        'mb-4 rounded-lg border p-4 sm:p-5 transition-all',
        isDark ? 'bg-[#111827] shadow-[0_2px_12px_rgba(0,0,0,0.2)]' : 'bg-white shadow-sm',
        hasError
          ? 'border-red-500/60 rfq-shake shadow-[0_0_0_3px_rgba(239,68,68,0.15)]'
          : isDark
            ? 'border-white/[0.08]'
            : 'border-[#0088cc]/20'
      ),
    vendorTitle: cn('text-lg font-bold tracking-tight sm:text-xl', isDark ? 'text-white' : 'text-[hsl(var(--foreground))]'),
    vendorSubtitle: isDark ? 'text-xs text-[#0088cc]/65' : 'text-xs text-[hsl(var(--muted-foreground))]',
    vendorFilters: cn(
      'mb-4 flex flex-wrap items-center gap-3 rounded-lg border p-2.5',
      isDark ? 'border-white/[0.08] bg-[#0f172a]/60' : 'border-[#0088cc]/20 bg-[#0088cc]/5'
    ),
    vendorSearch: cn(
      'w-full rounded-md border py-2 pl-10 pr-3 text-[13px] font-medium uppercase tracking-tight shadow-sm transition-all focus:outline-none focus:ring-1',
      isDark
        ? 'border-white/[0.08] bg-[#0f172a] text-slate-100 placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc]/45 focus:ring-[#0088cc]/25'
        : 'border-[#cbd5e1] bg-white text-[#0f172a] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:ring-[#0088cc]/20'
    ),
    vendorSelect: cn(
      'w-full appearance-none cursor-pointer rounded-md border px-3 py-2 text-[13px] font-medium uppercase tracking-tight shadow-sm focus:outline-none',
      isDark
        ? 'border-white/[0.08] bg-[#0f172a] text-slate-100 focus:border-[#0088cc]/45'
        : 'border-[#cbd5e1] bg-white text-[#0f172a] focus:border-[#0088cc]'
    ),
    vendorCard: (isSelected: boolean) =>
      cn(
        'relative flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-all duration-200 group',
        isSelected
          ? isDark
            ? 'border-[#0088cc]/50 bg-[#0088cc]/10 shadow-sm ring-1 ring-[#0088cc]/20'
            : 'border-[#0088cc] bg-blue-50/80 shadow-sm ring-1 ring-[#0088cc]/15'
          : isDark
            ? 'border-white/[0.08] bg-[#0f172a]/40 hover:border-white/[0.14] hover:bg-[#0f172a]/70'
            : 'border-[#0088cc]/20 bg-white hover:border-[#cbd5e1] hover:bg-[#0088cc]/5'
      ),
    vendorLogoWrap: (isSelected: boolean) =>
      cn(
        'flex h-10 w-10 items-center justify-center rounded-md border p-1.5 transition-colors',
        isSelected
          ? isDark
            ? 'border-[#0088cc]/40 bg-[#111827]'
            : 'border-[#0088cc]/30 bg-white'
          : isDark
            ? 'border-white/[0.08] bg-[#111827]/80'
            : 'border-[#0088cc]/20 bg-[#0088cc]/5'
      ),
    vendorName: (isSelected: boolean) =>
      cn(
        'mb-0.5 truncate text-[13px] font-bold tracking-tight',
        isSelected ? (isDark ? 'text-[#38bdf8]' : 'text-[#0088cc]') : isDark ? 'text-slate-100' : 'text-[hsl(var(--foreground))]'
      ),
    vendorCheck: (isSelected: boolean) =>
      cn(
        'flex h-4 w-4 items-center justify-center rounded-full border-2 transition-all',
        isSelected
          ? 'border-[#0088cc] bg-[#0088cc]'
          : isDark
            ? 'border-white/20 bg-[#0f172a] group-hover:border-white/35'
            : 'border-[#cbd5e1] bg-white group-hover:border-[#0088cc]/50'
      ),
    vendorEmptyIcon: cn(
      'mb-4 flex h-16 w-16 items-center justify-center rounded-full border',
      isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-[#0088cc]/5'
    ),
    vendorEmptyTitle: isDark ? 'text-white' : 'text-[hsl(var(--foreground))]',
    vendorEmptyText: isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]',
    vendorFooter: cn('mt-5 flex flex-col items-center justify-between gap-3 border-t pt-4 sm:flex-row', isDark ? 'border-white/[0.08]' : 'border-[#0088cc]/20'),
    vendorAvatar: cn(
      'flex h-8 w-8 items-center justify-center rounded-full border-2 text-[10px] font-black shadow',
      isDark ? 'border-[#111827] bg-[#0f172a] text-[#0088cc]/45' : 'border-white bg-[#0088cc]/10 text-[hsl(var(--muted-foreground))]'
    ),
    tableHead: cn(
      'border-y text-[10px] font-bold uppercase tracking-widest',
      isDark ? 'border-white/[0.08] bg-[#0f172a]/80 text-[#0088cc]/65' : 'border-[#0088cc]/20 bg-[#0088cc]/5 text-[hsl(var(--muted-foreground))]'
    ),
    tableBody: isDark ? 'divide-y divide-white/[0.06]' : 'divide-y divide-slate-100',
    tableRowNum: isDark ? 'text-[hsl(var(--muted-foreground))]' : 'text-[#0088cc]/65',
    tableField: tableFieldBase,
    tableFieldTextarea: cn(
      tableFieldBase,
      'px-2.5 py-1.5 text-[12px] min-h-[44px] normal-case'
    ),
    tableFieldCenter: cn(tableFieldBase, 'px-2 py-1.5 text-[12px] text-center normal-case'),
    tableSelect: cn(
      'w-full cursor-pointer rounded border px-2 py-1.5 text-[12px] font-medium uppercase focus:outline-none',
      isDark
        ? 'border-white/[0.08] bg-[#0f172a] text-slate-100 focus:border-[#0088cc]/40'
        : 'border-[#cbd5e1] bg-white text-[#0f172a] focus:border-[#0088cc]'
    ),
    itemsFooter: cn(
      'mt-3 flex items-center justify-between rounded-md border px-3 py-2.5',
      isDark ? 'border-white/[0.08] bg-[#0f172a]/60' : 'border-[#0088cc]/20 bg-[#0088cc]/5'
    ),
    itemsFooterText: isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]',
    addItemBtn: isDark
      ? 'rounded-md border border-[#0088cc]/50 bg-[#0088cc]/10 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-[#38bdf8] transition-all hover:bg-[#0088cc]/20'
      : 'rounded-md border border-[#0088cc] bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-[#0088cc] transition-all hover:bg-blue-50',
    deleteRowBtn: isDark
      ? 'rounded p-1.5 text-[hsl(var(--muted-foreground))] transition-all hover:bg-rose-500/10 hover:text-rose-400'
      : 'rounded p-1.5 text-[#0088cc]/65 transition-all hover:bg-red-50 hover:text-red-600',
    searchIcon: isDark ? 'text-[hsl(var(--muted-foreground))] group-focus-within:text-[#38bdf8]' : 'text-[#0088cc]/65 group-focus-within:text-[#0088cc]',
    locationIcon: isDark ? 'text-[hsl(var(--muted-foreground))]' : 'text-[#0088cc]/65',
    vendorIcon: (isSelected: boolean) =>
      isSelected ? (isDark ? 'text-[#38bdf8]' : 'text-[#0088cc]') : isDark ? 'text-[hsl(var(--muted-foreground))]' : 'text-[#0088cc]/65',
  };
}
